import { useEffect, useState } from 'react';
import { db } from '../../config/firebase';
import { collection, getDocs, updateDoc, doc } from 'firebase/firestore';
import { User as AppUser } from '../../types/user';
import { useAdminGuard } from '../../hooks/useAdminGuard';

const roleOptions = ['User', 'Trainee', 'Admin'];

const UsersAdminPage = () => {
  const { user, loading, isAdmin } = useAdminGuard({ redirectIfNotAdmin: true });
  const [users, setUsers] = useState<AppUser[]>([]);
  const [updating, setUpdating] = useState<string | null>(null);

  useEffect(() => {
    if (!user || !isAdmin) return;
    const fetchUsers = async () => {
      const querySnapshot = await getDocs(collection(db, 'users'));
      const usersList: AppUser[] = [];
      querySnapshot.forEach((docSnap) => {
        console.log(docSnap.data());
        usersList.push({ ...(docSnap.data() as AppUser), uid: docSnap.id });
      });
      setUsers(usersList);
    };
    fetchUsers();
  }, [user, isAdmin]);

  const handleVerifyToggle = async (uid: string, verified: boolean) => {
    setUpdating(uid);
    await updateDoc(doc(db, 'users', uid), { verified });
    setUsers((prev) => prev.map(u => u.uid === uid ? { ...u, verified } : u));
    setUpdating(null);
  };

  const handleRoleChange = async (uid: string, role: string) => {
    setUpdating(uid);
    await updateDoc(doc(db, 'users', uid), { role });
    setUsers((prev) => prev.map(u => u.uid === uid ? { ...u, role } : u));
    setUpdating(null);
  };

  if (loading || !user || !isAdmin) return (
    <div className="flex justify-center items-center h-40">
      <svg className="animate-spin h-8 w-8 text-teal-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
      </svg>
      <span className="ml-3 text-teal-700 text-sm">Loading users...</span>
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto py-6 px-1 sm:py-10 sm:px-4">
      <h1 className="text-xl xs:text-2xl sm:text-3xl font-bold text-teal-700 mb-4 xs:mb-6 sm:mb-8">User Management Dashboard</h1>
      <div className="overflow-x-auto rounded-lg shadow border border-stone-200 bg-white">
        <table className="min-w-[400px] xs:min-w-[500px] sm:min-w-[600px] md:min-w-full divide-y divide-stone-200 text-[10px] xs:text-[11px] sm:text-xs md:text-sm">
          <thead className="bg-stone-50">
            <tr>
              <th className="px-1 py-2 xs:px-2 xs:py-2 sm:px-6 sm:py-3 text-left text-[9px] xs:text-[10px] sm:text-xs font-medium text-stone-500 uppercase tracking-wider">Name</th>
              <th className="px-1 py-2 xs:px-2 xs:py-2 sm:px-6 sm:py-3 text-left text-[9px] xs:text-[10px] sm:text-xs font-medium text-stone-500 uppercase tracking-wider">Email</th>
              <th className="px-1 py-2 xs:px-2 xs:py-2 sm:px-6 sm:py-3 text-left text-[9px] xs:text-[10px] sm:text-xs font-medium text-stone-500 uppercase tracking-wider">Role</th>
              <th className="px-1 py-2 xs:px-2 xs:py-2 sm:px-6 sm:py-3 text-left text-[9px] xs:text-[10px] sm:text-xs font-medium text-stone-500 uppercase tracking-wider">Verified</th>
              <th className="px-1 py-2 xs:px-2 xs:py-2 sm:px-6 sm:py-3 text-left text-[9px] xs:text-[10px] sm:text-xs font-medium text-stone-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-stone-100">
            {users.map((u) => (
              <tr key={u.uid} className="hover:bg-stone-50">
                <td className="px-1 py-2 xs:px-2 xs:py-2 sm:px-6 sm:py-4 whitespace-nowrap font-medium text-stone-800 break-all">{u.fullName}</td>
                <td className="px-1 py-2 xs:px-2 xs:py-2 sm:px-6 sm:py-4 whitespace-nowrap text-stone-600 break-all">{u.email}</td>
                <td className="px-1 py-2 xs:px-2 xs:py-2 sm:px-6 sm:py-4 whitespace-nowrap">
                  <select
                    className="border border-stone-300 rounded px-1 py-1 xs:px-2 xs:py-1 text-[10px] xs:text-[11px] sm:text-xs md:text-sm focus:ring-teal-500 focus:border-teal-500"
                    value={u.role}
                    onChange={e => handleRoleChange(u.uid, e.target.value)}
                    disabled={updating === u.uid}
                  >
                    {roleOptions.map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </td>
                <td className="px-1 py-2 xs:px-2 xs:py-2 sm:px-6 sm:py-4 whitespace-nowrap">
                  <label className="inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="form-checkbox h-3.5 w-3.5 xs:h-4 xs:w-4 sm:h-5 sm:w-5 text-teal-600"
                      checked={!!u.verified}
                      onChange={e => handleVerifyToggle(u.uid, e.target.checked)}
                      disabled={updating === u.uid}
                    />
                    <span className="ml-2 text-[10px] xs:text-[11px] sm:text-sm text-stone-700">{u.verified ? 'Verified' : 'Unverified'}</span>
                  </label>
                </td>
                <td className="px-1 py-2 xs:px-2 xs:py-2 sm:px-6 sm:py-4 whitespace-nowrap">
                  {updating === u.uid && (
                    <span className="text-xs text-teal-600 animate-pulse">Updating...</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default UsersAdminPage; 